// ============================================================================
// HCAI Reporting Tool - Calculation Utilities
// Device-days, Infection Rates, SSI Rates, and Statistical Analysis
// ============================================================================

import type {
  DeviceSnapshot,
  HCAIRecord,
  SSISurveillance,
  DeviceDays,
  HCAIRates,
  SSIRate,
  PeriodMetrics,
  HCAICaseType,
  DeviceType,
  HAIRecord,
  HCAICases,
  HCAISurveillanceCase
} from '@/types';

/**
 * Calculate total device-days for a given period from device snapshots
 * Device-days = sum of patients with device for each day in the period
 */
export function calculateDeviceDays(snapshots: DeviceSnapshot[]): DeviceDays {
  const deviceDays: DeviceDays = {
    cvp: 0,
    pl: 0,
    urinaryCatheter: 0,
    ventilator: 0
  };

  for (const snapshot of snapshots) {
    deviceDays.cvp += snapshot.patientsWithDevice.cvp;
    deviceDays.pl += snapshot.patientsWithDevice.pl;
    deviceDays.urinaryCatheter += snapshot.patientsWithDevice.urinaryCatheter;
    deviceDays.ventilator += snapshot.patientsWithDevice.ventilator;
  }

  return deviceDays;
}

/**
 * Calculate HCAI infection rates per 1000 device-days
 * Rate = (cases / device-days) * 1000
 * Returns 0 if device-days is 0 (to avoid division by zero)
 */
export function calculateHCAIRates(
  cases: HCAIRecord['cases'],
  deviceDays: DeviceDays
): HCAIRates {
  return {
    uti: deviceDays.urinaryCatheter > 0
      ? (cases.uti / deviceDays.urinaryCatheter) * 1000
      : 0,
    ssiSuperficial: 0, // SSI rates calculated separately per procedures
    ssiDeep: 0,
    ssiOrganSpace: 0,
    hapOther: deviceDays.cvp > 0
      ? (cases.hapOther / deviceDays.cvp) * 1000
      : 0,
    vap: deviceDays.ventilator > 0
      ? (cases.vap / deviceDays.ventilator) * 1000
      : 0,
    bsiOther: deviceDays.cvp > 0
      ? (cases.bsiOther / deviceDays.cvp) * 1000
      : 0,
    cabsi: deviceDays.cvp > 0
      ? (cases.cabsi / deviceDays.cvp) * 1000
      : 0,
    sepsis: 0, // Sepsis rates calculated per admissions
    other: 0
  };
}

// Backward compatibility
export function calculateInfectionRates(
  cases: HAIRecord['cases'] | any,
  deviceDays: DeviceDays
): HCAIRates {
  return calculateHCAIRates(cases as any, deviceDays);
}

/**
 * Calculate SSI rate per 100 procedures
 * Rate = (infections / procedures) * 100
 */
export function calculateSSIRate(ssiData: SSISurveillance): SSIRate {
  const rate = ssiData.procedures > 0
    ? (ssiData.infections / ssiData.procedures) * 100
    : 0;

  return {
    rate,
    procedures: ssiData.procedures,
    infections: ssiData.infections
  };
}

/**
 * Aggregate HCAI cases from multiple records
 */
export function aggregateHCAICases(records: HCAIRecord[]): HCAICases {
  const aggregated: HCAICases = {
    uti: 0,
    ssiSuperficial: 0,
    ssiDeep: 0,
    ssiOrganSpace: 0,
    hapOther: 0,
    vap: 0,
    bsiOther: 0,
    cabsi: 0,
    sepsis: 0,
    other: 0
  };

  for (const record of records) {
    aggregated.uti += record.cases.uti || 0;
    aggregated.ssiSuperficial += record.cases.ssiSuperficial || 0;
    aggregated.ssiDeep += record.cases.ssiDeep || 0;
    aggregated.ssiOrganSpace += record.cases.ssiOrganSpace || 0;
    aggregated.hapOther += record.cases.hapOther || 0;
    aggregated.vap += record.cases.vap || 0;
    aggregated.bsiOther += record.cases.bsiOther || 0;
    aggregated.cabsi += record.cases.cabsi || 0;
    aggregated.sepsis += record.cases.sepsis || 0;
    aggregated.other += record.cases.other || 0;
  }

  return aggregated;
}

/**
 * Aggregate infections recorded via the comprehensive surveillance form
 */
export function aggregateHCAISurveillanceCases(
  cases: HCAISurveillanceCase[]
): HCAICases {
  const aggregated: HCAICases = {
    uti: 0,
    ssiSuperficial: 0,
    ssiDeep: 0,
    ssiOrganSpace: 0,
    hapOther: 0,
    vap: 0,
    bsiOther: 0,
    cabsi: 0,
    sepsis: 0,
    other: 0
  };

  for (const entry of cases) {
    if (!entry?.infections) continue;

    for (const infection of entry.infections) {
      switch (infection?.infectionType) {
        case 'uti':
          aggregated.uti += 1;
          break;
        case 'ssi': {
          const ssiType = infection.ssi?.type;
          if (ssiType === 'deep') {
            aggregated.ssiDeep += 1;
          } else if (ssiType === 'organSpace') {
            aggregated.ssiOrganSpace += 1;
          } else {
            aggregated.ssiSuperficial += 1;
          }
          break;
        }
        case 'pneumonia':
          if (infection.pneumoniaType === 'vap') {
            aggregated.vap += 1;
          } else {
            aggregated.hapOther += 1;
          }
          break;
        case 'bsi':
          if (infection.bsiType === 'cabsi') {
            aggregated.cabsi += 1;
          } else {
            aggregated.bsiOther += 1;
          }
          break;
        case 'sepsis':
          aggregated.sepsis += 1;
          break;
        case 'other':
          aggregated.other += 1;
          break;
        default:
          break;
      }
    }
  }

  return aggregated;
}

// Backward compatibility
export function aggregateHAICases(records: HAIRecord[]): any {
  return aggregateHCAICases(records as any);
}

/**
 * Calculate comprehensive period metrics
 */
export function calculatePeriodMetrics(
  wardId: string,
  periodStart: string,
  periodEnd: string,
  deviceSnapshots: DeviceSnapshot[],
  hcaiRecords: HCAIRecord[],
  ssiRecords?: SSISurveillance[]
): PeriodMetrics {
  const deviceDays = calculateDeviceDays(deviceSnapshots);
  const cases = aggregateHCAICases(hcaiRecords);
  const rates = calculateHCAIRates(cases, deviceDays);

  const metrics: PeriodMetrics = {
    wardId,
    periodStart,
    periodEnd,
    deviceDays,
    cases,
    rates
  };

  // Calculate SSI rate if data is available
  if (ssiRecords && ssiRecords.length > 0) {
    const totalProcedures = ssiRecords.reduce((sum, r) => sum + r.procedures, 0);
    const totalInfections = ssiRecords.reduce((sum, r) => sum + r.infections, 0);

    metrics.ssiRate = {
      rate: totalProcedures > 0 ? (totalInfections / totalProcedures) * 100 : 0,
      procedures: totalProcedures,
      infections: totalInfections
    };
  }

  return metrics;
}

/**
 * Calculate mean of an array of numbers
 */
export function calculateMean(values: number[]): number {
  if (values.length === 0) return 0;
  const sum = values.reduce((acc, val) => acc + val, 0);
  return sum / values.length;
}

/**
 * Calculate standard deviation
 */
export function calculateStandardDeviation(values: number[]): number {
  if (values.length === 0) return 0;

  const mean = calculateMean(values);
  const squaredDiffs = values.map(value => Math.pow(value - mean, 2));
  const avgSquaredDiff = calculateMean(squaredDiffs);

  return Math.sqrt(avgSquaredDiff);
}

/**
 * Determine if a rate exceeds the HCAI alert threshold
 * Threshold = mean + (numStdDev * standard deviation)
 */
export function shouldTriggerAlert(
  currentRate: number,
  historicalRates: number[],
  numStdDev: number = 2,
  minimumDataPoints: number = 8
): { shouldAlert: boolean; expectedRate: number; stdDev: number } {
  // Need minimum historical data points
  if (historicalRates.length < minimumDataPoints) {
    return {
      shouldAlert: false,
      expectedRate: 0,
      stdDev: 0
    };
  }

  const mean = calculateMean(historicalRates);
  const stdDev = calculateStandardDeviation(historicalRates);
  const threshold = mean + (numStdDev * stdDev);

  return {
    shouldAlert: currentRate > threshold,
    expectedRate: mean,
    stdDev
  };
}

/**
 * Map device type to HCAI case type
 */
export function mapDeviceToHCAIType(deviceType: DeviceType): HCAICaseType {
  const mapping: Record<DeviceType, HCAICaseType> = {
    urinaryCatheter: 'uti',
    cvp: 'cabsi',
    pl: 'bsiOther',
    ventilator: 'vap',
    cSection: 'ssiSuperficial', // Default to superficial, can be refined
    surgery: 'ssiSuperficial'
  };

  return mapping[deviceType];
}

/**
 * Map HCAI case type to device type
 */
export function mapHCAITypeToDevice(hcaiType: HCAICaseType): DeviceType | null {
  const mapping: Record<HCAICaseType, DeviceType | null> = {
    uti: 'urinaryCatheter',
    ssiSuperficial: 'cSection', // Default, could also be surgery
    ssiDeep: 'cSection',
    ssiOrganSpace: 'cSection',
    hapOther: 'ventilator',
    vap: 'ventilator',
    bsiOther: 'pl',
    cabsi: 'cvp',
    sepsis: null, // Not associated with specific device
    other: null
  };

  return mapping[hcaiType];
}

// Backward compatibility
export function mapDeviceToHAIType(deviceType: DeviceType): any {
  return mapDeviceToHCAIType(deviceType);
}

export function mapHAITypeToDevice(haiType: any): DeviceType | null {
  return mapHCAITypeToDevice(haiType);
}

/**
 * Round a number to specified decimal places
 */
export function roundTo(value: number, decimals: number = 2): number {
  const multiplier = Math.pow(10, decimals);
  return Math.round(value * multiplier) / multiplier;
}

/**
 * Format rate for display (rounded to 2 decimal places)
 */
export function formatRate(rate: number): string {
  return roundTo(rate, 2).toFixed(2);
}

/**
 * Calculate percentage change between two values
 */
export function calculatePercentageChange(oldValue: number, newValue: number): number {
  if (oldValue === 0) return newValue > 0 ? 100 : 0;
  return ((newValue - oldValue) / oldValue) * 100;
}

/**
 * Calculate moving average over N periods
 */
export function calculateMovingAverage(values: number[], windowSize: number): number[] {
  if (values.length < windowSize) return values;

  const movingAverages: number[] = [];

  for (let i = 0; i <= values.length - windowSize; i++) {
    const window = values.slice(i, i + windowSize);
    movingAverages.push(calculateMean(window));
  }

  return movingAverages;
}

/**
 * Validate denominator meets minimum threshold for rate calculation
 */
export function meetsMinimumDenominator(
  denominator: number,
  minimumThreshold: number = 20
): boolean {
  return denominator >= minimumThreshold;
}

/**
 * Get rate with validation message
 */
export function getRateWithValidation(
  numerator: number,
  denominator: number,
  minimumThreshold: number = 20
): { rate: number; isValid: boolean; message?: string } {
  if (denominator === 0) {
    return {
      rate: 0,
      isValid: false,
      message: 'No device-days recorded'
    };
  }

  if (!meetsMinimumDenominator(denominator, minimumThreshold)) {
    return {
      rate: (numerator / denominator) * 1000,
      isValid: false,
      message: `Denominator (${denominator}) below minimum threshold (${minimumThreshold})`
    };
  }

  return {
    rate: (numerator / denominator) * 1000,
    isValid: true
  };
}

/**
 * Calculate days between device insertion date and recording date
 * Returns the number of whole days (rounding down)
 * Example: inserted 2024-01-01, recorded 2024-01-05 = 4 days
 */
export function calculateDeviceDaysFromInsertion(
  insertionDate: string,
  recordingDate: string
): number {
  try {
    const insertion = new Date(insertionDate);
    const recording = new Date(recordingDate);

    // Ensure both dates are valid
    if (isNaN(insertion.getTime()) || isNaN(recording.getTime())) {
      return 0;
    }

    // Calculate difference in milliseconds
    const diffMs = recording.getTime() - insertion.getTime();

    // Convert to days (divide by milliseconds per day)
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    // Return at least 1 day if same date or later date
    return Math.max(1, days);
  } catch (error) {
    console.error('Error calculating device days:', error);
    return 0;
  }
}

/**
 * Calculate device-days for a single patient device
 * If removal date exists, use it; otherwise use today's date
 */
export function calculatePatientDeviceDays(
  insertionDate: string,
  removalDate?: string
): number {
  try {
    const insertion = new Date(insertionDate);
    const endDate = removalDate ? new Date(removalDate) : new Date();

    // Ensure both dates are valid
    if (isNaN(insertion.getTime()) || isNaN(endDate.getTime())) {
      return 0;
    }

    // Calculate difference in milliseconds
    const diffMs = endDate.getTime() - insertion.getTime();

    // Convert to days (divide by milliseconds per day)
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    // Return at least 1 day if same date
    return Math.max(1, days);
  } catch (error) {
    console.error('Error calculating patient device days:', error);
    return 0;
  }
}

/**
 * Calculate total device-days for a specific device type from patient records
 * Aggregates all patients' device-days for that device type in a given period
 */
export function calculateDeviceDaysFromPatients(
  patientRecords: any[], // Array of PatientRecord
  deviceType: DeviceType,
  periodStart: string,
  periodEnd: string
): number {
  const start = new Date(periodStart);
  const end = new Date(periodEnd);

  let totalDeviceDays = 0;

  for (const patient of patientRecords) {
    // Only include patients active during the period
    const admissionDate = new Date(patient.admissionDate);
    const dischargeDate = patient.dischargeDate ? new Date(patient.dischargeDate) : null;

    // Check if patient was active during period
    const patientActive =
      admissionDate <= end &&
      (!dischargeDate || dischargeDate >= start);

    if (!patientActive) continue;

    // Find devices of the specified type
    for (const device of patient.devices || []) {
      if (device.deviceType === deviceType) {
        const insertionDate = new Date(device.insertionDate);
        const removalDate = device.removalDate ? new Date(device.removalDate) : null;

        // Calculate overlap with period
        const effectiveStart = insertionDate > start ? insertionDate : start;
        const effectiveEnd = removalDate && removalDate < end ? removalDate : end;

        if (effectiveStart <= effectiveEnd) {
          const diffMs = effectiveEnd.getTime() - effectiveStart.getTime();
          const days = Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end day
          totalDeviceDays += Math.max(1, days);
        }
      }
    }
  }

  return totalDeviceDays;
}
